import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

import store from '../store';
import deepParseJson from '../utils/deepParseJson';
import { signOutSuccess, tokenRefreshed } from '../store/auth/sessionSlice';
import { shouldRefreshToken, refreshAccessToken, isRefreshTokenValid } from './tokenRefresh';

const unauthorizedCode = [401];
const authFailureCodes = [401, 403];
export const TOKEN_TYPE = 'Bearer ';
export const PERSIST_STORE_NAME = 'admin';
export const REQUEST_HEADER_AUTH_KEY = 'Authorization';

// Only sign the user out when the refresh endpoint says the token is
// actually invalid (401/403). Network errors, timeouts, and 5xx responses
// are transient and must NOT clear a valid refresh token.
const isAuthFailure = (error) =>
  authFailureCodes.includes(error?.response?.status);

export const API_URL = process.env.REACT_APP_API_URL;

const BaseService = axios.create({
  timeout: 60000,
  baseURL: API_URL
});

BaseService.interceptors.request.use(
  async (config) => {
    // Priority 1: Check Redux store first (updated synchronously on login)
    const { auth } = store.getState();
    let accessToken = auth.session.token;
    let { refreshToken } = auth.session;

    // Priority 2: Fall back to localStorage if Redux is empty (e.g., during rehydration)
    if (!accessToken) {
      const rawPersistData = localStorage.getItem(
        PERSIST_STORE_NAME
      );
      const persistData = deepParseJson(rawPersistData);
      accessToken = persistData?.auth?.session?.token;
      refreshToken = persistData?.auth?.session?.refreshToken;
    }

    // Check if we need to refresh the access token
    if (accessToken && refreshToken && shouldRefreshToken(accessToken)) {
      // Verify refresh token is still valid before attempting refresh
      if (isRefreshTokenValid(refreshToken)) {
        try {
          const refreshResult = await refreshAccessToken(refreshToken);

          // Persist the new access token (and the rotated refresh token, if
          // the backend issued one — without this, the next refresh 401s).
          store.dispatch(tokenRefreshed(refreshResult));

          // Use new token for this request
          accessToken = refreshResult.access;
        } catch (error) {
          // Only sign out if the refresh endpoint rejected the token.
          // Transient failures (network, 5xx, timeout) leave the session intact
          // so the next request can retry once the backend recovers.
          if (isAuthFailure(error)) {
            store.dispatch(signOutSuccess());
          }
          return Promise.reject(error);
        }
      } else {
        // Refresh token is invalid or expired, log user out
        store.dispatch(signOutSuccess());
        return config;
      }
    }

    if (accessToken) {
      try {
        // Check if token is expired before using it
        const decodedToken = jwtDecode(accessToken);
        const currentTime = Math.floor(Date.now() / 1000);

        // If token is expired, don't attach it to the request
        if (decodedToken.exp && decodedToken.exp < currentTime) {
          // Token expired - skip attaching Authorization header
          // This allows non-critical requests (like analytics) to fail gracefully
          return config;
        }

        // Token is valid - attach it to the request
        // eslint-disable-next-line no-param-reassign
        config.headers[REQUEST_HEADER_AUTH_KEY] =
          `${TOKEN_TYPE}${accessToken}`;
      } catch (error) {
        // If token decode fails, skip attaching it
        // console.error('[Website BaseService] Error decoding token:', error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

BaseService.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config: originalRequest } = error;

    // If 401 and we haven't retried yet, try refreshing token
    if (
      response &&
      unauthorizedCode.includes(response.status) &&
      !originalRequest.retryAttempted
    ) {
      originalRequest.retryAttempted = true;

      const { auth } = store.getState();
      const { refreshToken } = auth.session;

      // Try to refresh token one time
      if (refreshToken && isRefreshTokenValid(refreshToken)) {
        try {
          const refreshResult = await refreshAccessToken(refreshToken);

          // Persist the new access token (and the rotated refresh token, if any).
          store.dispatch(tokenRefreshed(refreshResult));

          // Retry original request with new token
          originalRequest.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${refreshResult.access}`;
          return axios(originalRequest);
        } catch (refreshError) {
          // Only sign out if the refresh endpoint rejected the token.
          // Transient failures leave the session intact for the next attempt.
          if (isAuthFailure(refreshError)) {
            store.dispatch(signOutSuccess());
          }
          return Promise.reject(refreshError);
        }
      } else {
        // No valid refresh token, log out
        store.dispatch(signOutSuccess());
      }
    }

    return Promise.reject(error);
  }
);

export default BaseService;
